import {
	Component,
	h,
	Prop,
	Element,
	State,
	Event,
	EventEmitter,
	Method,
	Listen,
	ComponentInterface,
	Host,
} from '@stencil/core';
import { TabTransitionType } from '../../_types/string-literals';
import { TimerFactory } from '../../../../common/_helpers/timer';
import { isIe } from '../../../../common/_helpers/browsers';
import ScrollingHelper from '../../_helpers/scrolling';
import trim from 'lodash/trim';
import { ComponentWithScrollingInterface } from '../../_interfaces/component-with-scrolling.interface';
import { getChildComponents } from '../../_helpers/prefixes';
import {
	IsharesStandardBackgroundColor,
	IsharesTabColor,
} from '../../../../../types/colors';
import {
	moveInlineStylesToHead,
	removeInlineStylesFromHead,
} from '../../_helpers/content-security-policy';

const KeycodeEnd = 35;
const KeycodeHome = 36;
const KeycodeArrowLeft = 37;
const KeycodeArrowRight = 39;
const KeycodeSpace = 32;
const KeycodeEnter = 13;
const TabCountForFlexGrowLabels = 2;

// noinspection JSIgnoredPromiseFromCall
@Component({
	tag: 'ds-tab-group',
	shadow: false,
})
export class TabGroup
	implements ComponentInterface, ComponentWithScrollingInterface
{
	@Prop({ reflect: true, mutable: true }) brand = 'ishares';
	@Prop({ mutable: true }) color: IsharesStandardBackgroundColor = '';
	@Prop({ mutable: true }) tabColor: IsharesTabColor = 'black';
	@Prop({ mutable: true }) transitionType: TabTransitionType = 'hideAndFadeIn';
	@Prop({ reflect: true, mutable: true }) labelWidth:
		| 'standard'
		| 'full-width' = 'standard';
	@Prop({ mutable: true }) animateTabs = true;
	@Prop({ mutable: true }) showIcon = false;
	@Prop({ mutable: true }) iconPosition: 'leading' | 'top' = 'leading';
	@Prop({ mutable: true }) iconAppearance: 'standard' | 'hero' = 'standard';
	@Prop() overflowButtonOverrideColor:
		| ''
		| 'blue'
		| 'purple'
		| 'pink'
		| 'green'
		| 'yellow'
		| 'gray' = '';
	@Element() el: HTMLDsTabGroupElement;
	@Event() tabSelected: EventEmitter<{
		index: number;
		tab: HTMLDsTabElement;
	}>;
	@Event() tabTransitionComplete: EventEmitter<{
		index: number;
		tab: HTMLDsTabElement;
	}>;
	@State() tabs: Array<HTMLDsTabElement> = [];
	@State() focusedIndex = 0;
	@State() selectedIndex: number = null;
	@State() prevSelectedIndex: number = null;
	@State() selectedItem: HTMLDsTabElement = null;
	@State() contentHeight: number = null;
	@State() showArrowLeft = false;
	@State() showArrowRight = true;

	scrollableItems: Array<HTMLElement> = [];
	scrollingContainer: HTMLElement;
	scrollingHelper;
	@State() activeTransitions: Promise<void>[] = [];

	@Method()
	async selectTab(newTabIndex: number): Promise<number> {
		this.prevSelectedIndex = this.selectedIndex;
		this.selectedIndex = newTabIndex;
		this.scrollingHelper.focusOnLabel(newTabIndex);
		await this.updateTabpanels();
		return newTabIndex;
	}

	@Listen('resize', { target: 'window' })
	async windowResizeHandler(): Promise<null> {
		return this.scrollingHelper.windowResizeHandler();
	}

	componentWillLoad(): void {
		this.scrollingHelper = new ScrollingHelper(this);
		const children = getChildComponents<HTMLDsTabElement>(this.el, 'ds-tab');

		children.forEach((tab, i) => {
			this.tabs.push(tab);

			if (tab.transitionType === undefined) {
				tab.transitionType = this.transitionType;
			}

			// Required for rerender
			if (tab.selected) {
				this.selectedIndex = i;
			}

			tab.index = i.toString();
		});
	}

	componentDidLoad() {
		if (this.tabColor === 'white') {
			this.el.querySelectorAll('.ds-tab-label-button').forEach((button) => {
				button.classList.add('ds-disable-unselected-animation');
			});
		}

		this.scrollableItems = Array.from(
			this.el.querySelectorAll<HTMLElement>('.ds-tab-label-button')
		);

		this.selectFirstItemByDefault();
		this.showArrowRight = this.scrollingHelper.shouldRightArrowBeShown();

		/**
		 * If there is hash initial update will not be called because selectTab function will call it.
		 * Witout this conditional updateTabpanels method is called twice immediatelly, once by selectTab
		 * and second time by componentDidLoad. This breaks hidding of initial visible tab(thats first tab by default).
		 * We are avoiding this issue with this if statement.
		 */
		if (!this.isThereValidHash()) {
			this.updateTabpanels();
		}

		setTimeout(() => {
			moveInlineStylesToHead(this.el.id);
		}, 700);
	}

	componentDidUpdate() {
		setTimeout(() => {
			moveInlineStylesToHead(this.el.id);
		}, 700);
	}

	disconnectedCallback() {
		removeInlineStylesFromHead(this.el.id);
	}

	/**
	 * Function checks is there url hash with same name as label or legend of tab
	 * If there at least one it will return true. Function uses window.parent.location.hash
	 * because in dev environment we are working in iframe(storybook use it) and window.location.hash will
	 * point to iframe's location hash not to main window one. If there is no iframe,
	 * window.parent.location is equal to window.location. This makes debugging easier and doesn't change
	 * functionality
	 *
	 * @returns boolean
	 */
	isThereValidHash() {
		const globalWindow = window.parent || window;

		const escapedHash = globalWindow.location.hash.replace('#', '');

		if (!escapedHash) return false;

		return this.tabs.some((el) => {
			const name = el.legend || el.label;
			return encodeURIComponent(name.replace(/ /g, '-')) === escapedHash;
		});
	}

	/**
	 * Handle clicking on an individual tab label
	 */
	handleTabLabelClick(e: MouseEvent, i: number) {
		e.preventDefault();
		this.prevSelectedIndex = this.selectedIndex;
		this.selectedIndex = i;
		this.focusedIndex = i;
		this.scrollingHelper.blurFocusForSafari(e.currentTarget as HTMLElement);
		this.scrollingHelper.animateSelectedItemIntoView();

		this.updateTabpanels();
	}

	/**
	 * This function is invoked every time the keyboard focus lands on a hidden button that is prepended before the visible
	 * tab buttons. This hidden button's purpose is to allow us to take the focus away from a button after a click so as to
	 * avoid the visible focus state from being shown unless the user is actually using the keyboard to navigate
	 *
	 * This function aims to handle the situation where the focus is arriving from en element before this tab group.
	 */
	handleHiddenButtonFocus(event: FocusEvent): void {
		if (isIe()) {
			// Too hard to optimise for IE, not worth doing.
			return;
		}

		// If event.relatedTarget is not a match for one of the tab buttons in this tab group
		// event.relatedTarget will be a <button> element if it was a click (and you would never want to forward the focus)
		// event.relatedTarget will be a div.button-container if it was a tab or a shift+tab in which case we need to determine if it came from this tab group, or perhaps a different level
		// if it wasn't one of the above then it's definitely going to need forwarding as it came from before the tab group
		// Then, we forward the focus to the tab button that was last in focus in this group

		const previouslyFocusedEl = event.relatedTarget as HTMLElement;

		if (previouslyFocusedEl === null) {
			this.scrollingHelper.focusOnLabel(this.focusedIndex);
		} else {
			const tabGroupTagName = this.el.tagName.toLowerCase();
			const parentTabGroup = previouslyFocusedEl.closest(tabGroupTagName);
			if (parentTabGroup != this.el) {
				this.scrollingHelper.focusOnLabel(this.focusedIndex);
			}
		}
	}

	handleKeyDown(e: KeyboardEvent): void {
		// TODO: Add support for newer e.key and/or e.code (will still need to support legacy keyCode too)
		switch (e.keyCode) {
			case KeycodeHome:
				this.scrollingHelper.focusOnLabel(0);
				break;
			case KeycodeEnd:
				this.scrollingHelper.focusOnLabel(this.tabs.length - 1);
				break;
			case KeycodeArrowLeft:
				if (this.focusedIndex <= 0) {
					this.scrollingHelper.focusOnLabel(this.tabs.length - 1);
				} else {
					this.scrollingHelper.focusOnLabel(this.focusedIndex - 1);
				}
				break;
			case KeycodeArrowRight:
				if (this.focusedIndex >= this.tabs.length - 1) {
					this.scrollingHelper.focusOnLabel(0);
				} else {
					this.scrollingHelper.focusOnLabel(this.focusedIndex + 1);
				}
				break;
			case KeycodeSpace:
			case KeycodeEnter:
				this.prevSelectedIndex = this.selectedIndex;
				this.selectedIndex = this.focusedIndex;
				this.updateTabpanels().then(() =>
					this.tabSelected.emit({
						index: this.selectedIndex,
						tab: this.tabs[this.selectedIndex],
					})
				);
				e.preventDefault();
				break;
		}
	}

	selectFirstItemByDefault(): void {
		if (!this.selectedIndex && this.tabs[0]) {
			this.selectedIndex = 0;
			this.selectedItem = this.tabs[0];
		}
	}

	async updateTabpanels(): Promise<void> {
		const visibleTab = this.tabs[this.selectedIndex];
		const prevTab = this.tabs[this.prevSelectedIndex];

		if (prevTab) {
			await prevTab.hide();
		}

		if (this.animateTabs) {
			if (visibleTab && typeof visibleTab.measureHeight === 'function') {
				this.contentHeight = await visibleTab.measureHeight();
			}

			const clock = TimerFactory(50);
			await clock.tickTock();
			if (visibleTab && typeof visibleTab.measureHeight === 'function') {
				this.contentHeight = await visibleTab.measureHeight();
			}
			this.tabSelected.emit({
				index: this.selectedIndex,
				tab: visibleTab,
			});
			if (visibleTab && typeof visibleTab.fadeIn === 'function') {
				await visibleTab.fadeIn();
			}
			this.contentHeight = null;
		} else {
			if (visibleTab && typeof visibleTab.show === 'function') {
				await visibleTab.show();
			}
		}
		this.tabTransitionComplete.emit({
			index: this.selectedIndex,
			tab: visibleTab,
		});

		if (visibleTab) {
			const videoBlocks = visibleTab.querySelectorAll('ds-video-block');
			videoBlocks.forEach((videoBlock) => {
				(videoBlock as any).setMinHeightOfVideoContent();
				const videoPlaylist = videoBlock.querySelector('ds-video-playlist');
				if (videoPlaylist) {
					videoPlaylist.recalculateArrowsVisibility();
				}
			});
		}
	}

	getHeightStyle(): { height?: string } {
		let style: { height?: string } = {};
		if (this.contentHeight) {
			style.height = this.contentHeight.toString() + 'px';
		} else style.height = 'auto';
		return style;
	}

	renderTabLabelButtons(tabs: HTMLDsTabElement[]) {
		const buttonClasses = {};
		let validIcons = true; // all tabs have icons

		if (this.showIcon) {
			// verify all tabs have an icon
			tabs.forEach((tab) => {
				const icon = trim(tab.getAttribute('icon'));
				if (icon === '') {
					validIcons = false;
				}
			});

			buttonClasses[`ds-tab-icon-${this.iconPosition}`] = validIcons;
		}

		buttonClasses['ds-tab-hero'] = this.iconAppearance === 'hero';
		buttonClasses['ds-tab-label-button'] = true;
		buttonClasses['ds-flex-grow-small-screens'] =
			this.tabs.length === TabCountForFlexGrowLabels;

		return tabs.map((tab, i) => {
			let icon = null;

			if (this.showIcon && validIcons) {
				icon = trim(tab.getAttribute('icon'));
				if (icon.substring(0, 2) !== 'fa') {
					// Example: if user input `band-aid` in DCR - transform to `fa-band-aid`
					icon = `fa-${icon}`;
				}
			}

			const selected = this.scrollingHelper.isSelected(i);
			const color = selected && this.tabColor !== 'black' ? this.tabColor : '';
			buttonClasses[`ds-button-label-${i}`] = true;
			buttonClasses[`ds-button-label-${i - 1}`] = false;

			return (
				<ds-button
					class={buttonClasses}
					color={color}
					type="primary"
					selected={selected}
					canReceiveFocus={selected ? true : false}
					onClick={(e) => this.handleTabLabelClick(e, i)}
					accessibilityRole="tab"
					accessibilitySelected={
						this.scrollingHelper.isSelected(i) ? true : false
					}
					accessibilityControlsId={tab.id}
				>
					{icon !== null && <span class={`fa ${icon}`}></span>}
					<span>{tab.label}</span>
				</ds-button>
			);
		});
	}

	render() {
		const bgColorClass = this.color ? `ds-bg-ishares-${this.color}` : '';

		const showTabLabels = this.tabs.length > 1;

		const tabLabels = this.renderTabLabelButtons(this.tabs);

		const arrowButtonColor =
			this.tabColor === 'white' ? 'black' : this.overflowButtonOverrideColor;

		const tabGroupHeader = showTabLabels ? (
			<div class="row">
				<div class="ds-col col">
					<section class="ds-scrolling-arrows-wrapper">
						<nav
							class="ds-tab-labels ds-scrolling-items"
							ref={(el) => (this.scrollingContainer = el as HTMLElement)}
							onScroll={() => this.scrollingHelper.handleScroll()}
							role="tablist"
							onKeyDown={(e) => this.handleKeyDown(e)}
						>
							<a
								class="ds-receive-focus"
								onFocus={(e) => this.handleHiddenButtonFocus(e)}
							/>
							<ds-arrow-button
								direction="left"
								button-color={arrowButtonColor}
								class={{ 'd-none': !this.showArrowLeft }}
								onClick={() => this.scrollingHelper.handleLeftArrowClick()}
							/>
							<ds-arrow-button
								direction="right"
								button-color={arrowButtonColor}
								class={{ 'd-none': !this.showArrowRight }}
								onClick={() => this.scrollingHelper.handleRightArrowClick()}
							/>
							{tabLabels}
						</nav>
					</section>
				</div>
			</div>
		) : (
			''
		);

		return (
			<Host>
				<div class={`container-fluid ${bgColorClass}`}>
					<section class="ds-tab-group-header">{tabGroupHeader}</section>
					<section
						class={{
							'ds-tab-group-body': true,
							row: this.labelWidth !== 'full-width',
						}}
					>
						<div
							data-chromatic="ignore"
							class={{
								'ds-tabs-height-transition': true,
								'ds-col col': this.labelWidth !== 'full-width',
							}}
							style={this.getHeightStyle()}
						>
							<slot />
						</div>
					</section>
				</div>
			</Host>
		);
	}
}
